import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from '@mantine/core';
import ReactCountryFlag from 'react-country-flag';
import { getStoredLanguageOrderForSwitcher } from '../services/languageDetection';
import './LanguageSwitcher.css';

const LANGUAGES = [
  { code: 'cs', label: 'Čeština', countryCode: 'CZ' },
  { code: 'da', label: 'Dansk', countryCode: 'DK' },
  { code: 'de', label: 'Deutsch', countryCode: 'DE' },
  { code: 'en', label: 'English', countryCode: 'GB' },
  { code: 'es', label: 'Español', countryCode: 'ES' },
  { code: 'fr', label: 'Français', countryCode: 'FR' },
  { code: 'hi', label: 'हिन्दी', countryCode: 'IN' },
  { code: 'hr', label: 'Hrvatski', countryCode: 'HR' },
  { code: 'hu', label: 'Magyar', countryCode: 'HU' },
  { code: 'it', label: 'Italiano', countryCode: 'IT' },
  { code: 'ja', label: '日本語', countryCode: 'JP' },
  { code: 'ko', label: '한국어', countryCode: 'KR' },
  { code: 'nb', label: 'Norsk bokmål', countryCode: 'NO' },
  { code: 'nl', label: 'Nederlands', countryCode: 'NL' },
  { code: 'pl', label: 'Polski', countryCode: 'PL' },
  { code: 'pt', label: 'Português', countryCode: 'PT' },
  { code: 'ro', label: 'Română', countryCode: 'RO' },
  { code: 'sv', label: 'Svenska', countryCode: 'SE' },
  { code: 'tr', label: 'Türkçe', countryCode: 'TR' },
  { code: 'zh-CN', label: '中文 (简体)', countryCode: 'CN' },
];

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [opened, setOpened] = useState(false);
  const [orderedLanguages, setOrderedLanguages] = useState(LANGUAGES);
  
  const currentLang = orderedLanguages.find(lang => lang.code === i18n.language) || orderedLanguages[0];

  // Load language order from localStorage on component mount
  useEffect(() => {
    const storedOrder = getStoredLanguageOrderForSwitcher();
    if (storedOrder) {
      // Create a map for quick lookup
      const langMap = new Map(LANGUAGES.map(lang => [lang.code, lang]));
      
      // Sort languages according to stored order
      const ordered = storedOrder
        .map(code => langMap.get(code))
        .filter(Boolean) as typeof LANGUAGES;
      
      // Add any languages not in the stored order at the end
      const remaining = LANGUAGES.filter(lang => !storedOrder.includes(lang.code));
      
      setOrderedLanguages([...ordered, ...remaining]);
    }
  }, []);

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
  };

  const items = orderedLanguages.map((lang) => (
    <Menu.Item
      key={lang.code}
      onClick={() => handleLanguageChange(lang.code)}
      leftSection={
        <ReactCountryFlag
          countryCode={lang.countryCode}
          svg
          style={{
            width: '1.25em',
            height: '1.25em',
            marginRight: '0.5rem',
          }}
          title={lang.countryCode}
        />
      }
    >
      {lang.label}
    </Menu.Item>
  ));

  return (
    <Menu
      onOpen={() => setOpened(true)}
      onClose={() => setOpened(false)}
      radius="md"
      withinPortal
      zIndex={999999}
    >
      <Menu.Target>
        <button
          type="button"
          className="header-theme-toggle-button"
          aria-label={currentLang.label}
        >
          <ReactCountryFlag
            countryCode={currentLang.countryCode}
            svg
            style={{
              width: '1.5em',
              height: '1.5em',
            }}
            title={currentLang.label}
          />
        </button>
      </Menu.Target>
      <Menu.Dropdown>{items}</Menu.Dropdown>
    </Menu>
  );
}
